import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { SesionService, refrescoRechazado } from '../sesion/sesion.service';
import { SesionStore } from '../sesion/sesion.store';
import { SIN_AUTENTICACION } from './contexto';

/**
 * Autenticación de cada request y refresco ante un 401.
 *
 * En IRIX esto vivía dentro de `HttpClientService`, y por eso `httpResource()` y
 * cualquier uso directo de `HttpClient` se quedaban sin autenticar. Acá es un
 * interceptor: **todo** lo que salga por `HttpClient` va autenticado, venga de
 * donde venga.
 *
 * El reintento es **uno solo**. Si el request reintentado vuelve a dar 401, el
 * error se propaga: insistir sería un bucle.
 *
 * Si el refresco se **rechaza**, lo que se propaga es el 401 original y no el
 * error del refresh: `SesionService` ya llevó al login (o a `/sin-acceso`) y
 * `erroresInterceptor` no avisa los 401. Con el error del refresh (un 400
 * `invalid_grant`) salía además el aviso "El pedido tiene datos inválidos".
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.context.get(SIN_AUTENTICACION)) return next(req);

  const store = inject(SesionStore);
  const sesion = inject(SesionService);

  const autenticar = (original: HttpRequest<unknown>): HttpRequest<unknown> => {
    const token = store.accessToken();
    if (!token) return original;
    return original.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'x-permiso': store.idPermiso(),
      },
    });
  };

  return next(autenticar(req)).pipe(
    catchError((error: unknown) => {
      const esNoAutorizado = error instanceof HttpErrorResponse && error.status === 401;
      if (!esNoAutorizado) return throwError(() => error);

      return from(sesion.refrescar()).pipe(
        catchError((errorRefresco: unknown) =>
          throwError(() => (refrescoRechazado(errorRefresco) ? error : errorRefresco)),
        ),
        // Con el token nuevo se rearma el request: `autenticar` vuelve a leer el
        // store, así que toma el token recién guardado.
        switchMap(() => next(autenticar(req))),
      );
    }),
  );
};
